/**
 * An allowlisted snapshot of persisted UI state, for attaching to a
 * feedback report.
 *
 * Much of what a user means by "the screen was set up like this" already
 * lives in localStorage via `useStoredState` / `LocalStorageHandler`, so
 * reading it back costs nothing per page. The catch is what else is in
 * there.
 *
 * SECURITY — do not replace this with a wholesale dump.
 * `LocalStorageHandler.getCognitoLastAuthUser()` reads
 * `CognitoIdentityServiceProvider.*` keys, and that namespace also holds
 * the user's id and refresh tokens. A blind dump would copy live
 * credentials into a feedback record that is then read by whoever triages
 * it — a different person, possibly on a different team. Two independent
 * guards below: an allowlist of prefixes, and a denylist that wins over
 * the allowlist. Adding a key means adding it to ALLOWED_KEY_PREFIXES on
 * purpose.
 */

/** UI-preference namespaces. Extend deliberately, never with a wildcard. */
const ALLOWED_KEY_PREFIXES = [
  'runAuxSensor.',
  'runList.',
  'runPlotter.',
  'sessionCharts.',
  'sessionView.',
  'plotter.',
  'view-selected',
];

/**
 * Anything matching these is dropped even if a prefix above would admit
 * it. This is the guard that must hold if the allowlist is ever widened
 * carelessly.
 */
const DENY_PATTERNS = [/cognito/i, /token/i, /secret/i, /password/i, /credential/i, /auth/i, /session[-_]?id/i];

const MAX_VALUE_CHARS = 300;

export interface StorageSnapshot {
  values: Record<string, string>;
  /** How many keys were present but not captured — makes the filtering visible. */
  skipped_key_count: number;
}

function isAllowed(key: string): boolean {
  if (DENY_PATTERNS.some((pattern) => pattern.test(key))) return false;
  return ALLOWED_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/**
 * Read the allowlisted subset of localStorage. Never throws — a browser
 * with storage disabled or a quota error must not stop a user filing a
 * report about it.
 */
export function readStorageSnapshot(): StorageSnapshot {
  const values: Record<string, string> = {};
  let skipped = 0;
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key) continue;
      if (!isAllowed(key)) {
        skipped += 1;
        continue;
      }
      const raw = localStorage.getItem(key) ?? '';
      values[key] = raw.length > MAX_VALUE_CHARS ? `${raw.slice(0, MAX_VALUE_CHARS)}…` : raw;
    }
  } catch (error) {
    console.warn('Could not read storage snapshot for feedback:', error);
  }
  return { values, skipped_key_count: skipped };
}
